import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ClockIcon, CheckIcon, OrdersIcon, DownloadIcon } from '../UI/Icons';
import vendorService from '../../services/vendorService';
import { Vendor } from '../../utils/constants';
import orderService, { Order } from '../../services/orderService';
import toastService from '../../services/toastService';
import StatTile from '../Dashboard/StatTile';
import Modal from '../UI/Modal';
import { getOrderDurationMinutes, parseOrderDate } from '../../utils/orderDates';
import styles from './Orders.module.css';

type DateRange = 'today' | 'yesterday' | 'week' | 'all';
type StatusFilter = 'all' | 'active' | Order['status'];

interface OrdersProps {
  promotions?: unknown[];
  viewMode?: string;
}

const STATUS_TABS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'preparing', label: 'Preparing' },
  { id: 'ready', label: 'Ready' },
  { id: 'out-for-delivery', label: 'Out for Delivery' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'cancelled', label: 'Cancelled' },
];

const POLL_INTERVAL_MS = 15000;
const URGENT_PENDING_MS = 5 * 60 * 1000;

const demoCustomers = [
  'Alex Johnson', 'Sarah Williams', 'Mike Chen', 'Emily Davis', 'Chris Wilson',
  'Jessica Brown', 'David Martinez', 'Lisa Anderson', 'Ryan Taylor', 'Amanda Garcia',
];

const isDev = process.env.NODE_ENV === 'development';

function getStatusClass(status: string): string {
  switch (status) {
    case 'pending': return styles.statusPending;
    case 'confirmed': return styles.statusConfirmed;
    case 'preparing': return styles.statusPreparing;
    case 'ready': return styles.statusReady;
    case 'out-for-delivery':
    case 'on-delivery':
      return styles.statusOutForDelivery;
    case 'delivered': return styles.statusDelivered;
    case 'cancelled': return styles.statusCancelled;
    default: return styles.statusPending;
  }
}

function getNextStatus(currentStatus: string): Order['status'] {
  switch (currentStatus) {
    case 'pending': return 'confirmed';
    case 'confirmed': return 'preparing';
    case 'preparing': return 'ready';
    case 'ready': return 'out-for-delivery';
    case 'out-for-delivery':
    case 'on-delivery':
      return 'delivered';
    default: return currentStatus as Order['status'];
  }
}

function getStatusText(status: string): string {
  switch (status) {
    case 'pending': return 'Pending';
    case 'confirmed': return 'Confirmed';
    case 'preparing': return 'Preparing';
    case 'ready': return 'Ready';
    case 'out-for-delivery':
    case 'on-delivery':
      return 'Out for Delivery';
    case 'delivered': return 'Delivered';
    case 'cancelled': return 'Cancelled';
    default: return status;
  }
}

function getPlacedTime(order: Order): number {
  const parsed = parseOrderDate(order.placedAt || order.createdAt);
  return parsed ? parsed.getTime() : 0;
}

function getCompletedTime(order: Order): number {
  const parsed = parseOrderDate(
    order.deliveredAt || order.completedAt || order.readyAt || order.createdAt || order.placedAt
  );
  return parsed ? parsed.getTime() : 0;
}

function getOrderDate(order: Order): Date | null {
  return parseOrderDate(order.placedAt || order.createdAt);
}

function matchesDateRange(order: Order, range: DateRange, reference: Date): boolean {
  if (range === 'all') return true;

  const orderDate = getOrderDate(order);
  // Keep undated orders visible so they don't disappear from the queue.
  if (!orderDate) return true;

  const start = new Date(reference);
  start.setHours(0, 0, 0, 0);
  const end = new Date(reference);
  end.setHours(23, 59, 59, 999);

  if (range === 'today') {
    return orderDate >= start && orderDate <= end;
  }

  if (range === 'yesterday') {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
    end.setHours(23, 59, 59, 999);
    return orderDate >= start && orderDate <= end;
  }

  if (range === 'week') {
    const weekStart = new Date(reference);
    weekStart.setDate(weekStart.getDate() - 7);
    weekStart.setHours(0, 0, 0, 0);
    return orderDate >= weekStart && orderDate <= end;
  }

  return true;
}

function matchesSearch(order: Order, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  return (
    (order.orderNumber || '').toLowerCase().includes(q) ||
    (order.customerName || '').toLowerCase().includes(q) ||
    (order.vendorName || '').toLowerCase().includes(q)
  );
}

function normalizeStatus(status: string): string {
  if (status === 'on-delivery') return 'out-for-delivery';
  return status;
}

function matchesStatusFilter(order: Order, filter: StatusFilter): boolean {
  const status = normalizeStatus(order.status);
  if (filter === 'all') return true;
  if (filter === 'active') return status !== 'delivered' && status !== 'cancelled';
  return status === filter;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function getElapsedMs(order: Order, now: number): number | null {
  if (order.status === 'cancelled') return null;

  const placed = parseOrderDate(order.placedAt || order.createdAt);
  if (!placed) return null;
  const start = placed.getTime();

  if (order.status === 'pending') {
    return Math.max(0, now - start);
  }

  const activeStart = parseOrderDate(
    order.confirmedAt || order.preparingAt || order.placedAt || order.createdAt
  );
  const startMs = activeStart ? activeStart.getTime() : start;
  const delivered = parseOrderDate(order.deliveredAt || order.completedAt);
  const end = delivered ? delivered.getTime() : now;
  return Math.max(0, end - startMs);
}

function formatSeatLocation(seat?: Order['seatLocation']): string | null {
  if (!seat) return null;
  const parts: string[] = [];
  if (seat.section) parts.push(`Section ${seat.section}`);
  if (seat.row) parts.push(`Row ${seat.row}`);
  if (seat.seat) parts.push(`Seat ${seat.seat}`);
  return parts.length > 0 ? parts.join(', ') : null;
}

function sortOrders(list: Order[]): Order[] {
  return [...list].sort((a, b) => {
    const aDelivered = a.status === 'delivered';
    const bDelivered = b.status === 'delivered';

    if (!aDelivered && bDelivered) return -1;
    if (aDelivered && !bDelivered) return 1;

    if (aDelivered && bDelivered) {
      return getCompletedTime(b) - getCompletedTime(a);
    }

    return getPlacedTime(b) - getPlacedTime(a);
  });
}

const Orders: React.FC<OrdersProps> = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [now, setNow] = useState(Date.now());
  const [exporting, setExporting] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange>('week');
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [polling] = useState(true);

  const [pendingDelete, setPendingDelete] = useState<Order | null>(null);
  const [pendingCancel, setPendingCancel] = useState<Order | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const loadOrders = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      const data = await orderService.getAll({ limit: 100 });
      setOrders(data);
      setLastUpdated(Date.now());
    } catch (error) {
      console.error('Error loading orders:', error);
      toastService.error('Failed to load orders');
    } finally {
      if (isInitial) setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadOrders(true);
  }, [loadOrders]);

  useEffect(() => {
    if (!polling) return undefined;

    const tick = () => {
      if (document.hidden) return;
      if (bulkUpdating) return;
      if (selectedOrders.size > 0) return;
      loadOrders(false);
    };

    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [polling, bulkUpdating, selectedOrders.size, loadOrders]);

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const data = await vendorService.getAll();
        setVendors(data);
      } catch (error) {
        console.error('Error loading vendors:', error);
      }
    };
    loadVendors();
  }, []);

  const referenceDate = useMemo(() => new Date(), [dateRange]);

  const baseFilteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (vendorFilter !== 'all' && String(order.vendorId) !== String(vendorFilter)) return false;
      if (!matchesDateRange(order, dateRange, referenceDate)) return false;
      if (!matchesSearch(order, debouncedSearch)) return false;
      return true;
    });
  }, [orders, vendorFilter, dateRange, debouncedSearch, referenceDate]);

  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    STATUS_TABS.forEach((tab) => {
      if (tab.id === 'all') {
        counts.all = baseFilteredOrders.length;
      } else if (tab.id === 'active') {
        counts.active = baseFilteredOrders.filter(
          (o) => {
            const status = normalizeStatus(o.status);
            return status !== 'delivered' && status !== 'cancelled';
          }
        ).length;
      } else {
        counts[tab.id] = baseFilteredOrders.filter(
          (o) => normalizeStatus(o.status) === tab.id
        ).length;
      }
    });
    return counts;
  }, [baseFilteredOrders]);

  const visibleOrders = useMemo(() => {
    return sortOrders(
      baseFilteredOrders.filter((order) => matchesStatusFilter(order, statusFilter))
    );
  }, [baseFilteredOrders, statusFilter]);

  const stats = useMemo(() => {
    const scope = baseFilteredOrders;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const active = scope.filter(
      (o) => {
        const status = normalizeStatus(o.status);
        return status !== 'delivered' && status !== 'cancelled';
      }
    ).length;

    const deliveredToday = scope.filter((o) => {
      if (o.status !== 'delivered') return false;
      const delivered = parseOrderDate(o.deliveredAt || o.completedAt);
      return delivered !== null && delivered >= todayStart;
    }).length;

    const cancelled = scope.filter((o) => o.status === 'cancelled').length;

    const durations = scope
      .filter((o) => o.status === 'delivered')
      .map((o) =>
        getOrderDurationMinutes(o.placedAt || o.createdAt, o.deliveredAt || o.completedAt)
      )
      .filter((minutes): minutes is number => minutes !== null);

    const averageEndToEnd = durations.length > 0
      ? durations.reduce((sum, minutes) => sum + minutes, 0) / durations.length
      : 0;

    return { active, deliveredToday, cancelled, averageEndToEnd };
  }, [baseFilteredOrders]);

  const hasActiveFilters =
    statusFilter !== 'active' ||
    dateRange !== 'week' ||
    vendorFilter !== 'all' ||
    debouncedSearch.trim().length > 0;

  const isFilteredEmpty = orders.length > 0 && visibleOrders.length === 0;
  const isTrulyEmpty = orders.length === 0 && !loading;

  const clearFilters = () => {
    setStatusFilter('active');
    setDateRange('week');
    setVendorFilter('all');
    setSearchQuery('');
  };

  const showAllOrders = () => {
    setDateRange('all');
    setStatusFilter('all');
  };

  const updateOrderStatus = async (orderId: string, newStatus: Order['status']) => {
    try {
      const updatedOrder = await orderService.updateStatus(orderId, newStatus);
      setOrders((prev) => prev.map((order) =>
        order.id === orderId
          ? { ...order, ...updatedOrder, items: updatedOrder.items ?? order.items }
          : order
      ));
      toastService.success(`Order marked ${getStatusText(newStatus).toLowerCase()}`);
    } catch (error) {
      console.error('Error updating order status:', error);
      toastService.error('Failed to update order status');
    }
  };

  const confirmDeleteOrder = async () => {
    if (!pendingDelete?.id) return;
    try {
      setConfirmLoading(true);
      await orderService.delete(pendingDelete.id);
      setOrders((prev) => prev.filter((o) => o.id !== pendingDelete.id));
      toastService.success('Order deleted');
      setPendingDelete(null);
    } catch (error) {
      console.error('Error deleting order:', error);
      toastService.error('Failed to delete order');
    } finally {
      setConfirmLoading(false);
    }
  };

  const confirmCancelOrder = async () => {
    if (!pendingCancel?.id) return;
    try {
      setConfirmLoading(true);
      await updateOrderStatus(pendingCancel.id, 'cancelled');
      setPendingCancel(null);
    } catch {
      // updateOrderStatus handles toast
    } finally {
      setConfirmLoading(false);
    }
  };

  const generateTestOrder = async () => {
    if (!vendors.length) {
      toastService.error('No vendors available. Create a vendor with menu items first.');
      return;
    }

    const vendorsWithMenus = vendors.filter((v) => (v.menuItems || []).length > 0);
    if (!vendorsWithMenus.length) {
      toastService.error('No vendors have menu items.');
      return;
    }

    const randomVendor = vendorsWithMenus[Math.floor(Math.random() * vendorsWithMenus.length)];
    if (!randomVendor.id) {
      toastService.error('Selected vendor is missing an ID.');
      return;
    }

    const items: Order['items'] = [];
    let totalAmount = 0;
    const numItems = Math.floor(Math.random() * 3) + 1;

    for (let i = 0; i < numItems; i++) {
      const randomItem = randomVendor.menuItems![Math.floor(Math.random() * randomVendor.menuItems!.length)];
      const quantity = Math.floor(Math.random() * 3) + 1;
      items.push({
        menuItemId: randomItem.id?.toString() || `local-${i}`,
        name: randomItem.name,
        price: randomItem.price,
        quantity,
      });
      totalAmount += randomItem.price * quantity;
    }

    try {
      const created = await orderService.create({
        vendorId: randomVendor.id.toString(),
        vendorName: randomVendor.name,
        customerName: demoCustomers[Math.floor(Math.random() * demoCustomers.length)],
        items,
        totalAmount,
        status: 'pending',
      });
      setOrders((prev) => [created, ...prev]);
      toastService.success('Test order created');
    } catch (error) {
      console.error('Error creating test order:', error);
      toastService.error('Failed to create test order');
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportOrders = async () => {
    try {
      setExporting(true);
      const blob = await orderService.exportCsv();
      downloadBlob(blob, `orders-${new Date().toISOString().slice(0, 10)}.csv`);
      toastService.success('Orders exported');
    } catch (error) {
      console.error('Error exporting orders:', error);
      toastService.error('Failed to export orders');
    } finally {
      setExporting(false);
    }
  };

  const toggleSelect = (orderId: string, checked: boolean) => {
    setSelectedOrders((prev) => {
      const next = new Set(prev);
      if (checked) next.add(orderId);
      else next.delete(orderId);
      return next;
    });
  };

  const selectAllActive = () => {
    const activeIds = visibleOrders
      .filter((o) => o.status !== 'delivered' && o.status !== 'cancelled')
      .map((o) => o.id!)
      .filter(Boolean);
    setSelectedOrders(new Set(activeIds));
  };

  const clearSelection = () => setSelectedOrders(new Set());

  const bulkUpdateStatus = async (newStatus: Order['status']) => {
    if (selectedOrders.size === 0) return;
    try {
      setBulkUpdating(true);
      const updated = await orderService.bulkUpdateStatus(Array.from(selectedOrders), newStatus);
      setOrders((prev) => prev.map((o) => {
        const match = updated.find((u) => u.id === o.id);
        return match ? { ...o, ...match, items: match.items ?? o.items } : o;
      }));
      clearSelection();
      toastService.success(`${updated.length} order${updated.length !== 1 ? 's' : ''} updated`);
    } catch (error) {
      console.error('Error bulk updating orders:', error);
      toastService.error('Failed to bulk update orders');
    } finally {
      setBulkUpdating(false);
    }
  };

  const secondsAgo = lastUpdated ? Math.floor((now - lastUpdated) / 1000) : null;

  const OrderCard = ({ order }: { order: Order }) => {
    const elapsed = getElapsedMs(order, now);
    const isUrgent = order.status === 'pending' && elapsed !== null && elapsed > URGENT_PENDING_MS;
    const seatLabel = formatSeatLocation(order.seatLocation);
    const isActive = order.status !== 'delivered' && order.status !== 'cancelled';

    return (
      <div
        className={`${styles.orderCard} ${order.status === 'delivered' ? styles.orderCardCompleted : ''} ${isUrgent ? styles.orderCardUrgent : ''}`}
      >
        <div className={styles.cardSelect}>
          <input
            type="checkbox"
            aria-label={`Select order ${order.orderNumber}`}
            checked={selectedOrders.has(order.id!)}
            onChange={(e) => toggleSelect(order.id!, e.target.checked)}
          />
        </div>

        <div className={styles.orderHeader}>
          <div className={styles.orderInfo}>
            <h3 className={styles.orderNumber}>{order.orderNumber}</h3>
            <p className={styles.customerName}>{order.customerName}</p>
            {seatLabel && <p className={styles.seatInfo}>{seatLabel}</p>}
          </div>

          <div className={styles.orderTiming}>
            {elapsed !== null && (
              <div className={styles.timer}>
                <ClockIcon size={16} />
                <span className={styles.timerText}>{formatDuration(elapsed)}</span>
              </div>
            )}
            <div className={`${styles.status} ${getStatusClass(order.status)}`}>
              {getStatusText(order.status)}
            </div>
          </div>
        </div>

        <div className={styles.orderContent}>
          <div className={styles.vendorInfo}>
            <strong>{order.vendorName}</strong>
          </div>

          <div className={styles.orderItems}>
            {(order.items || []).map((item, index) => (
              <div key={index} className={styles.orderItem}>
                <span className={styles.itemName}>
                  {item.quantity}x {item.name}
                </span>
                <span className={styles.itemPrice}>
                  ${(item.price * item.quantity).toFixed(2)}
                </span>
              </div>
            ))}
          </div>

          <div className={styles.orderTotal}>
            <strong>Total: ${order.totalAmount.toFixed(2)}</strong>
          </div>
        </div>

        <div className={styles.orderActions}>
          {order.status === 'cancelled' ? (
            <button
              type="button"
              onClick={() => setPendingDelete(order)}
              className={styles.deleteButton}
            >
              Delete Order
            </button>
          ) : order.status === 'delivered' ? (
            <div className={styles.completedIndicator}>
              <CheckIcon size={16} />
              Order Delivered
            </div>
          ) : (
            <div className={styles.actionRow}>
              <button
                type="button"
                onClick={() => updateOrderStatus(order.id!, getNextStatus(order.status))}
                className={styles.statusButton}
              >
                <CheckIcon size={16} />
                {order.status === 'pending' && 'Confirm Order'}
                {order.status === 'confirmed' && 'Start Preparing'}
                {order.status === 'preparing' && 'Mark Ready'}
                {order.status === 'ready' && 'Mark Out for Delivery'}
                {(order.status === 'out-for-delivery' || order.status === 'on-delivery') && 'Mark Delivered'}
              </button>
              {isActive && (
                <button
                  type="button"
                  onClick={() => setPendingCancel(order)}
                  className={styles.cancelAction}
                >
                  Cancel Order
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const SkeletonGrid = () => (
    <div className={styles.ordersGrid}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={styles.skeletonOrder} aria-hidden="true">
          <div className={`${styles.skeletonBlock} ${styles.skeletonHeader}`} />
          <div className={styles.skeletonBody}>
            <div className={`${styles.skeletonBlock} ${styles.skeletonLine}`} />
            <div className={`${styles.skeletonBlock} ${styles.skeletonLine}`} />
            <div className={`${styles.skeletonBlock} ${styles.skeletonLineShort}`} />
          </div>
          <div className={`${styles.skeletonBlock} ${styles.skeletonAction}`} />
        </div>
      ))}
    </div>
  );

  const EmptyState = ({ filtered }: { filtered: boolean }) => (
    <div className={styles.empty}>
      <OrdersIcon size={48} className={styles.emptyIconSvg} />
      <h3 className={styles.emptyTitle}>
        {filtered ? 'No Matching Orders' : 'No Orders Yet'}
      </h3>
      <p className={styles.emptyDescription}>
        {filtered
          ? `No orders match the current filters (${dateRange === 'today' ? 'Today' : dateRange === 'week' ? 'Last 7 days' : dateRange}). Try widening the date range.`
          : 'Orders will appear here as customers place them.'}
      </p>
      {filtered ? (
        <div className={styles.emptyActions}>
          <button type="button" onClick={clearFilters} className={styles.emptyButton}>
            Reset Filters
          </button>
          <button type="button" onClick={showAllOrders} className={styles.emptyButtonSecondary}>
            Show All Time
          </button>
        </div>
      ) : isDev ? (
        <button type="button" onClick={generateTestOrder} className={styles.devLink}>
          [dev] Generate test order
        </button>
      ) : null}
    </div>
  );

  return (
    <div className={styles.orders}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>Orders</h1>
            {isDev && (
              <button type="button" onClick={generateTestOrder} className={styles.devLink}>
                [dev] Generate test order
              </button>
            )}
          </div>
          <p className={styles.subtitle}>
            Manage incoming orders and track delivery status
            {secondsAgo !== null && (
              <span className={styles.updatedAt}>
                {' '}· Updated {secondsAgo}s ago
                {refreshing ? ' (refreshing…)' : ''}
              </span>
            )}
          </p>
        </div>

        <div className={styles.headerActions}>
          <button
            type="button"
            onClick={() => loadOrders(false)}
            className={styles.refreshButton}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            onClick={exportOrders}
            className={styles.exportButton}
            disabled={exporting}
          >
            <DownloadIcon size={16} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      <div className={styles.statTileRow}>
        <StatTile
          icon={<OrdersIcon size={16} />}
          label="Active"
          value={stats.active}
          loading={loading && orders.length === 0}
        />
        <StatTile
          icon={<CheckIcon size={16} />}
          label="Delivered Today"
          value={stats.deliveredToday}
          trendTone="success"
          loading={loading && orders.length === 0}
        />
        <StatTile
          icon={<OrdersIcon size={16} />}
          label="Cancelled"
          value={stats.cancelled}
          trendTone="danger"
          loading={loading && orders.length === 0}
        />
        <StatTile
          icon={<ClockIcon size={16} />}
          label="Avg End-to-End"
          value={stats.averageEndToEnd > 0 ? `${stats.averageEndToEnd.toFixed(1)}m` : '—'}
          loading={loading && orders.length === 0}
        />
      </div>

      <div className={styles.filterRow}>
        <input
          type="search"
          className={styles.searchInput}
          placeholder="Search order #, customer, vendor…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search orders"
        />
        <select
          className={styles.filterSelect}
          value={vendorFilter}
          onChange={(e) => setVendorFilter(e.target.value)}
          aria-label="Filter by vendor"
        >
          <option value="all">All vendors</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
        <select
          className={styles.filterSelect}
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value as DateRange)}
          aria-label="Filter by date"
        >
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="week">Last 7 days</option>
          <option value="all">All time</option>
        </select>
      </div>

      <div className={styles.tabStrip} role="tablist" aria-label="Order status">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={statusFilter === tab.id}
            className={`${styles.tab} ${statusFilter === tab.id ? styles.tabActive : ''}`}
            onClick={() => setStatusFilter(tab.id)}
          >
            {tab.label}
            <span className={styles.tabCount}>{tabCounts[tab.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {selectedOrders.size > 0 && (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>
            {selectedOrders.size} order{selectedOrders.size !== 1 ? 's' : ''} selected
          </span>
          <div className={styles.bulkBarActions}>
            <button type="button" onClick={selectAllActive} className={styles.bulkBarBtn} disabled={bulkUpdating}>
              Select Active
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className={styles.bulkBarBtn}
              disabled={bulkUpdating}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => bulkUpdateStatus('ready')}
              className={`${styles.bulkBarBtn} ${styles.bulkBarBtnPrimary}`}
              disabled={bulkUpdating}
            >
              {bulkUpdating ? 'Updating…' : 'Mark Ready'}
            </button>
            <button
              type="button"
              onClick={() => bulkUpdateStatus('delivered')}
              className={`${styles.bulkBarBtn} ${styles.bulkBarBtnSuccess}`}
              disabled={bulkUpdating}
            >
              {bulkUpdating ? 'Updating…' : 'Mark Delivered'}
            </button>
          </div>
        </div>
      )}

      {loading && orders.length === 0 ? (
        <SkeletonGrid />
      ) : isTrulyEmpty ? (
        <EmptyState filtered={false} />
      ) : isFilteredEmpty ? (
        <EmptyState filtered={true} />
      ) : (
        <div className={styles.ordersGrid}>
          {visibleOrders.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      )}

      <Modal
        isOpen={!!pendingDelete}
        onClose={() => !confirmLoading && setPendingDelete(null)}
        title="Delete order?"
        size="sm"
        footer={
          <div className={styles.modalActions}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setPendingDelete(null)}
              disabled={confirmLoading}
            >
              Keep
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={confirmDeleteOrder}
              disabled={confirmLoading}
            >
              {confirmLoading ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        }
      >
        <p>
          Permanently delete cancelled order <strong>{pendingDelete?.orderNumber}</strong>?
        </p>
      </Modal>

      <Modal
        isOpen={!!pendingCancel}
        onClose={() => !confirmLoading && setPendingCancel(null)}
        title="Cancel order?"
        size="sm"
        footer={
          <div className={styles.modalActions}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setPendingCancel(null)}
              disabled={confirmLoading}
            >
              Keep order
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={confirmCancelOrder}
              disabled={confirmLoading}
            >
              {confirmLoading ? 'Cancelling…' : 'Cancel order'}
            </button>
          </div>
        }
      >
        <p>
          Cancel order <strong>{pendingCancel?.orderNumber}</strong> for {pendingCancel?.customerName}?
        </p>
      </Modal>
    </div>
  );
};

export default Orders;
